import { useCallback, useMemo, useState } from 'react';
import type { z } from 'zod';

/*
 * Validation on blur, not on keystroke — design-system.md §8.
 *
 * The distinction is not a detail. Validating as somebody types tells them
 * "That does not look like an email address" after the second character of
 * their own address, which reads as the form arguing with them. Waiting until
 * they leave the field means the message arrives when they have finished
 * saying the thing.
 *
 * **A field that has been submitted counts as touched.** Otherwise pressing the
 * button on an empty form does nothing visible, which is the worst outcome
 * available: the user pressed the thing and nothing happened.
 *
 * The schema is the same Zod object the API validates with (`@shared/schemas`),
 * so the browser and the server cannot disagree about what is required.
 */

export interface BlurValidation<T> {
  /** Errors for fields the user has finished with, keyed by field path. */
  errors: Record<string, string>;
  /** True when every field passes — the submit button reads this. */
  isValid: boolean;
  /** The parsed value, or null when it does not validate. */
  parsed: T | null;
  /** Call from `onBlur`. */
  touch: (field: string) => void;
  /** Call before submitting: marks everything touched so nothing hides. */
  touchAll: () => void;
  /** Reset after a successful submit. */
  reset: () => void;
}

export function useBlurValidation<S extends z.ZodTypeAny>(
  schema: S,
  values: unknown,
): BlurValidation<z.infer<S>> {
  const [touched, setTouched] = useState<Set<string> | 'all'>(new Set());

  const result = useMemo(() => schema.safeParse(values), [schema, values]);

  const allErrors = useMemo(() => {
    if (result.success) return {};

    const errors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      // First message per field. A second is usually a knock-on of the first,
      // and two messages under one input is noise.
      errors[path] ??= issue.message;
    }
    return errors;
  }, [result]);

  const errors = useMemo(() => {
    if (touched === 'all') return allErrors;

    const visible: Record<string, string> = {};
    for (const [path, message] of Object.entries(allErrors)) {
      if (touched.has(path)) visible[path] = message;
    }
    return visible;
  }, [allErrors, touched]);

  const touch = useCallback((field: string) => {
    setTouched((current) => {
      if (current === 'all') return current;
      if (current.has(field)) return current;
      const next = new Set(current);
      next.add(field);
      return next;
    });
  }, []);

  const touchAll = useCallback(() => {
    setTouched('all');
  }, []);

  const reset = useCallback(() => {
    setTouched(new Set());
  }, []);

  return {
    errors,
    isValid: result.success,
    parsed: result.success ? result.data : null,
    touch,
    touchAll,
    reset,
  };
}
